import React, {useState, useEffect, useCallback, useRef} from 'react';
import t from 'babel-plugin-yate/macro';
import {Field, useForm, useFormState} from 'react-final-form';
import {FieldArray} from 'react-final-form-arrays';
import {format, parseISO} from 'date-fns';

import {getRefinancingCredits} from '../../store/application';
import {parseRadioFormField, formatRON} from '../../lib/misc';
import {
  hasItems,
  composeFormValidators,
  isRequired,
} from '../../lib/validations';

import {Block} from '../../components/uiElem';
import {DocumentsUploadBox} from '../../components/DocumentsUploadBox';
import Label from '../../components/Form/Label';
import Condition from '../../components/Condition';
import Checkbox from '../../components/Form/Checkbox';
import RadioButton from '../../components/Form/RadioButton';
import ValidationError from '../../components/Form/ValidationError';
import {useDispatch, useSelector} from 'react-redux';
import {useUploadFile} from '../../lib/useUploadFile';
import Text from '../../components/uiElem/Text';

const DOC_TYPE_REFINANCING_LETTER = 'refinancing_letter';
const REFINANCING_MAXIMUM_TOTAL_AMOUNT = 45000;

const sumSelectedCredits = (credits, ids, field) =>
  credits
    .filter(c => ids?.includes(c.id))
    .reduce((sum, c) => sum + (c[field] || 0), 0);

const RefinancingCreditsForm = React.memo(
  ({disabled, uuid, onForceIbanChange}) => {
    const dispatch = useDispatch();
    const form = useForm();
    const [isLoading, setIsLoading] = useState(true);
    const fetchedFileIds = useRef(new Set());
    const pendingInitIds = useRef(null);

    const handleRefinancingCredits = useCallback(async () => {
      setIsLoading(true);
      const credits = await dispatch(getRefinancingCredits(uuid));

      if (credits && !credits.error) {
        pendingInitIds.current = credits
          .filter(el => el.refinancing_letter_document_id)
          .map(el => el.id);
      }

      setIsLoading(false);
    }, [uuid, dispatch]);

    useEffect(() => {
      if (isLoading || !pendingInitIds.current?.length) return;

      const ids = pendingInitIds.current;
      pendingInitIds.current = null;

      form.batch(() => {
        const {values} = form.getState();
        if (values.refinancing_credits_flow == null) {
          form.change('refinancing_credits_flow', true);
        }
        if (!values.selected_refinancing_contracts?.length) {
          ids.forEach(id => {
            form.mutators.push('selected_refinancing_contracts', id);
          });
        }
      });
    }, [isLoading, form]);

    const {file, uploading, uploaded, fetch, upload} = useUploadFile();

    const refinancingCredits = useSelector(
      state => state.application.refinancingCredits,
    );

    const {
      values: {selected_refinancing_contracts: selectedIds = []},
    } = useFormState({subscription: {values: true}});

    const selectedIdsKey = selectedIds.join(',');

    const totalBalance = sumSelectedCredits(
      refinancingCredits,
      selectedIds,
      'current_balance',
    );
    const totalMonthly = sumSelectedCredits(
      refinancingCredits,
      selectedIds,
      'monthly_payment_amt',
    );

    useEffect(() => {
      const bankIds = refinancingCredits
        .filter(c => selectedIds?.includes(c.id) && c.is_bank)
        .map(c => c.id);

      onForceIbanChange(new Set(bankIds));

      if (bankIds.length > 0) {
        form.change('payment', 'iban');
      }
    }, [
      selectedIdsKey,
      selectedIds,
      refinancingCredits,
      form,
      onForceIbanChange,
    ]);

    useEffect(() => {
      handleRefinancingCredits();
    }, [handleRefinancingCredits]);

    useEffect(() => {
      refinancingCredits.forEach(el => {
        const fileId = el.refinancing_letter_document_id;
        if (fileId && !fetchedFileIds.current.has(fileId)) {
          fetchedFileIds.current.add(fileId);
          fetch(DOC_TYPE_REFINANCING_LETTER, fileId);
        }
      });
    }, [refinancingCredits, fetch]);

    if (isLoading) {
      return (
        <div className="min-h-200 flex items-center justify-center text-30">
          <div className="spinner" />
        </div>
      );
    }

    return (
      <>
        <Block heading={t`Oferta de refinantare`}>
          <div className="pt-8 p-2">
            <div className="md:flex md:flex-wrap">
              <div className="w-full px-2 mb-4">
                <Label
                  value={t`Daca vrei sa refinantezi unul sau mai multe credite existente, selecteaza optiunea mai jos.
                            Vom completa automat detaliile creditelor tale.`}
                />
                <div className="flex mt-4">
                  <div className="pr-8">
                    <Field
                      name="refinancing_credits_flow"
                      component={RadioButton}
                      type="radio"
                      value={true}
                      disabled={disabled}
                      parse={parseRadioFormField}
                      validate={composeFormValidators(isRequired())}
                      label={t`Da, vreau oferta de refinantare`}
                    />
                  </div>

                  <div className="pr-8">
                    <Field
                      name="refinancing_credits_flow"
                      component={RadioButton}
                      type="radio"
                      value={false}
                      disabled={disabled}
                      parse={value => {
                        form.change('selected_refinancing_contracts', []);
                        onForceIbanChange(() => new Set([]));
                        return parseRadioFormField(value);
                      }}
                      label={t`Nu, oferta noua de credit`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Condition when="refinancing_credits_flow" is={true}>
            <div className="pt-8 p-2 border-t">
              <div className="w-full px-2 mb-4">
                <Label
                  value={t`Conturile tale de credit pe care le poti refinanta`}
                />
                <div className="md:flex md:flex-wrap">
                  <FieldArray
                    name="selected_refinancing_contracts"
                    validate={value => {
                      const itemsError = hasItems(
                        t`Selecteaza cel putin un credit de refinantat`,
                      )(value);
                      if (itemsError) return itemsError;
                      if (
                        sumSelectedCredits(
                          refinancingCredits,
                          value,
                          'current_balance',
                        ) > REFINANCING_MAXIMUM_TOTAL_AMOUNT
                      )
                        return t`Suma maxima de refinantat este ${formatRON(REFINANCING_MAXIMUM_TOTAL_AMOUNT)}`;
                    }}>
                    {({fields, meta}) => (
                      <>
                        {meta.error && meta.touched && (
                          <div className="w-full mt-1">
                            <ValidationError value={meta.error} />
                          </div>
                        )}

                        <div className="w-full mt-3">
                          {refinancingCredits?.map(
                            ({
                              id,
                              creditor,
                              account_no,
                              credit_type,
                              is_credex_ifn,
                              refinancing_letter_document_id,
                              current_balance,
                              credit_limit,
                              document_date,
                              remaining_duration,
                              monthly_payment_amt,
                            }) => {
                              const fieldValueIndex = fields.value?.findIndex(
                                selectedId => selectedId === id,
                              );

                              const fileKeyId =
                                refinancing_letter_document_id ??
                                `${DOC_TYPE_REFINANCING_LETTER}_${id}`;

                              return (
                                <div className="w-full pr-4 py-2" key={id}>
                                  <div className="flex justify-between">
                                    <Field
                                      name="selected_refinancing_contracts"
                                      label={
                                        <>
                                          <Text ui="mars">
                                            <div className="pr-2">{t`${credit_type}`}</div>
                                          </Text>
                                        </>
                                      }
                                      component={Checkbox}
                                      type="checkbox"
                                      checked={fieldValueIndex > -1}
                                      disabled={disabled}
                                      showError={false}
                                      onChange={({target: {checked}}) => {
                                        if (checked) {
                                          fields.push(id);
                                        } else {
                                          fields.remove(fieldValueIndex);
                                        }
                                      }}
                                    />

                                    <Text ui="mars">
                                      {formatRON(current_balance)}
                                    </Text>
                                  </div>

                                  <div className="ml-6 py-1">
                                    {!!credit_limit && (
                                      <div className="flex justify-between border-t py-1">
                                        <Text ui="uranus">
                                          <div className="pr-2">{t`Total imprumutat`}</div>
                                        </Text>
                                        <Text ui="uranus">
                                          {formatRON(credit_limit)}
                                        </Text>
                                      </div>
                                    )}

                                    {!!remaining_duration && (
                                      <div className="flex justify-between border-t py-1">
                                        <Text ui="uranus">
                                          <div className="pr-2">{t`Durata ramasa`}</div>
                                        </Text>
                                        <Text ui="uranus">
                                          {remaining_duration} {t`luni`}
                                        </Text>
                                      </div>
                                    )}

                                    {!!monthly_payment_amt && (
                                      <div className="flex justify-between border-t py-1">
                                        <Text ui="uranus">
                                          <div className="pr-2">{t`Plata lunara curenta`}</div>
                                        </Text>
                                        <Text ui="uranus">
                                          {formatRON(monthly_payment_amt)}
                                        </Text>
                                      </div>
                                    )}

                                    {!!document_date && !!credit_limit && (
                                      <div className="flex justify-between border-t py-1">
                                        <Text ui="uranus">
                                          <div className="pr-2">{t`Data acordarii`}</div>
                                        </Text>
                                        <Text ui="uranus">
                                          {format(
                                            parseISO(document_date),
                                            'dd.MM.yyyy',
                                          )}
                                        </Text>
                                      </div>
                                    )}

                                    {!!creditor && (
                                      <div className="flex justify-between border-t py-1">
                                        <Text ui="uranus">
                                          <div className="pr-2">{t`Creditor`}</div>
                                        </Text>
                                        <Text ui="uranus">{creditor}</Text>
                                      </div>
                                    )}

                                    {!!account_no && (
                                      <div className="flex justify-between border-t py-1">
                                        <Text ui="uranus">
                                          <div className="pr-2">{t`Numar contract`}</div>
                                        </Text>
                                        <Text ui="uranus">{account_no}</Text>
                                      </div>
                                    )}
                                  </div>

                                  {is_credex_ifn === false && (
                                    <Condition
                                      when="selected_refinancing_contracts"
                                      is={id}>
                                      <div className="ml-6 mt-2 border rounded">
                                        <DocumentsUploadBox
                                          acceptFileTypes="image/*,.pdf"
                                          description={
                                            <>
                                              <span className="block text-14 leading-normal font-medium pb-2">
                                                {t`Adresa de refinantare`}
                                              </span>
                                              <span className="block">
                                                {t`Accepted file types: ${'.pdf, .jpg, .jpeg, .png'}.`}
                                              </span>
                                            </>
                                          }
                                          isFileUploaded={uploaded[fileKeyId]}
                                          isFileMissing={!uploaded[fileKeyId]}
                                          isLoading={uploading[fileKeyId]}
                                          onUpload={f =>
                                            upload(
                                              f,
                                              DOC_TYPE_REFINANCING_LETTER,
                                              {
                                                eligible_credit_for_refinancing_id:
                                                  id,
                                              },
                                              fileKeyId,
                                            )
                                          }
                                          uploadInputLabel={t`Upload document`}
                                          documents={file[fileKeyId]}
                                          showDownloadLink={true}
                                        />
                                      </div>
                                    </Condition>
                                  )}
                                </div>
                              );
                            },
                          )}
                        </div>
                      </>
                    )}
                  </FieldArray>
                </div>
              </div>

              {selectedIds.length > 1 && (
                <div className="w-full px-2 mb-4 pt-4">
                  <div className="flex justify-between py-1 border-t">
                    <div className="pr-2">
                      <Label value={t`Total de refinantat`} />
                    </div>
                    <Text>{formatRON(totalBalance)}</Text>
                  </div>

                  <div className="flex justify-between py-1 border-t">
                    <div className="pr-2">
                      <Label value={t`Total plata curenta/luna `} />
                    </div>
                    <Text>{formatRON(totalMonthly)}</Text>
                  </div>
                </div>
              )}
            </div>
          </Condition>
        </Block>
      </>
    );
  },
);

export default RefinancingCreditsForm;
